import { Map as MapIcon, Satellite, Maximize2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { MAP_LAYERS } from "./mapLayers";

/** Frosted control surface used for everything floating on top of the map. */
export const Glass = ({ className, children, testId }) => (
  <div className={cn("pointer-events-auto rounded-full bg-card/85 shadow-[0_0_0_1px_rgba(16,24,40,0.05),0_6px_20px_-8px_rgba(16,24,40,0.28)] backdrop-blur-xl dark:bg-card/80", className)} data-testid={testId}>
    {children}
  </div>
);

const LAYER_ICON = { light: MapIcon, satellite: Satellite };

/** Segmented base-layer switch (Kart · Satellitt) – icon + label, the active segment is a dark pill. */
export const LayerToggle = ({ value, onChange, className }) => (
  <Glass className={cn("flex h-9 items-center gap-0.5 p-1", className)} testId="map-layer-toggle">
    {MAP_LAYERS.map((l) => {
      const Icon = LAYER_ICON[l.id] || MapIcon;
      const active = value === l.id;
      return (
        <button
          key={l.id}
          type="button"
          onClick={() => onChange(l.id)}
          aria-pressed={active}
          className={cn(
            "inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-[12px] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60",
            active ? "bg-foreground text-background font-medium shadow-[0_1px_2px_rgba(16,24,40,0.2)]" : "text-foreground/70 hover:bg-muted hover:text-foreground"
          )}
          data-testid={`map-layer-${l.id}`}
        >
          <Icon className={cn("h-3.5 w-3.5", active ? "opacity-90" : "text-muted-foreground")} strokeWidth={1.75} aria-hidden />
          {l.label}
        </button>
      );
    })}
  </Glass>
);

/** Provider attribution – required by Mapbox / OpenStreetMap terms. */
export const MapAttribution = ({ layer, className }) => (
  <div className={cn("pointer-events-auto rounded-md bg-card/70 px-1.5 py-0.5 text-[10.5px] leading-4 text-muted-foreground backdrop-blur", className)} data-testid="map-attribution">
    {layer?.attribution}
  </div>
);

export const LegendDot = ({ className, children }) => (
  <span className="inline-flex items-center gap-1.5 text-[11.5px] text-foreground/80">
    <span className={cn("h-2 w-2 rounded-full", className)} aria-hidden /> {children}
  </span>
);

/** Compact legend for the portfolio map. */
export const MapLegend = ({ className, items }) => (
  <Glass className={cn("flex h-9 items-center gap-3 px-3.5", className)} testId="map-legend">
    {items.map((it) => (
      <LegendDot key={it.label} className={it.dot}>
        {it.label}
      </LegendDot>
    ))}
  </Glass>
);

/** Small «open full map» affordance. */
export const MapLinkChip = ({ onClick, children, testId }) => (
  <Glass className="h-9">
    <button type="button" onClick={onClick} className="inline-flex h-9 items-center gap-1.5 rounded-full pl-3.5 pr-3 text-[12px] font-medium text-foreground/85 transition-colors duration-150 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60" data-testid={testId}>
      {children} <Maximize2 className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
    </button>
  </Glass>
);

/** Soft inner vignette – gives the map surface depth without touching the basemap itself. */
export const MapVignette = () => <div className="pointer-events-none absolute inset-0 z-[5] rounded-[inherit] shadow-[inset_0_0_0_1px_rgba(16,24,40,0.04),inset_0_0_80px_rgba(16,24,40,0.05)]" aria-hidden />;
